// Agent Spec / Build Queues: thin helpers over public.ai_jobs (no UI)

import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from './supabase';
import { setDeclaredLocksFromSpec, tryAcquireJobLocks } from './aiJobLocks';
import { formatForDateInput } from '../utils/dateUtils';
import { fetchTrelloCardUrl } from './trelloCardUrl';
import { recordCascadeCalls } from './securityCascade';
import type { AiJob, AiJobStatus, HandoffEntry } from './supabase.types';
import type { OutlineHeading, PageSection } from './aiJobSummary';

export type { AiJob, AiJobStatus };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type LooseClient = SupabaseClient<any, 'public', any>;

export const AI_JOB_STATUSES: AiJobStatus[] = [
  'spec_queued',
  'spec_in_progress',
  'spec_ready',
  'spec_revision',
  'build_queued',
  'build_in_progress',
  'build_ready',
  'build_revision',
  'build_staged',
  'unwind_queued',
  'unwind_in_progress',
  'unwind_ready',
  'approved',
  'approval_in_progress',
  'complete',
  'submitted',
  'cancelled',
];

export const CANCELABLE_SPEC_STATUSES: AiJobStatus[] = [
  'spec_queued',
  'spec_in_progress',
  'spec_ready',
  'spec_revision',
  'build_staged',
];

export const CANCELABLE_BUILD_STATUSES: AiJobStatus[] = ['build_ready'];

export const DELETABLE_STATUSES: AiJobStatus[] = ['unwind_ready'];

/** Owlbert watches these. Default queue list — pickup, not already claimed. */
export const OWL_WATCH_STATUSES: AiJobStatus[] = [
  'spec_queued',
  'spec_revision',
  'build_queued',
  'build_revision',
  'unwind_queued',
  'approved',
];

/** Spec phase, including in-progress and human review. Ready-for-review must not fall off this queue. */
export const SPEC_QUEUE_STATUSES: AiJobStatus[] = [
  'spec_queued',
  'spec_in_progress',
  'spec_ready',
  'spec_revision',
];

/** Build phase, including staged, approval, complete, in-progress, human review, and unwind. */
export const BUILD_QUEUE_STATUSES: AiJobStatus[] = [
  'build_queued',
  'build_in_progress',
  'build_ready',
  'build_revision',
  'build_staged',
  'unwind_queued',
  'unwind_in_progress',
  'unwind_ready',
  'approved',
  'approval_in_progress',
  'complete',
];

/** Statuses that may hold path/DB locks. Staged can still hold after a mid-build park. Submitted does not. */
export const ACTIVE_BUILD_STATUSES: AiJobStatus[] = [
  'build_queued',
  'build_in_progress',
  'build_ready',
  'build_revision',
  'build_staged',
  'unwind_queued',
  'unwind_in_progress',
  'unwind_ready',
  'approved',
  'approval_in_progress',
];

export const HUMAN_REVIEW_STATUSES: AiJobStatus[] = [
  'spec_ready',
  'build_ready',
  'unwind_ready',
  'complete',
];

export const CLAIMABLE_STATUSES: AiJobStatus[] = [
  'spec_queued',
  'spec_revision',
  'build_queued',
  'build_revision',
  'unwind_queued',
  'approved',
];

export const IN_PROGRESS_STATUSES: AiJobStatus[] = [
  'spec_in_progress',
  'build_in_progress',
  'unwind_in_progress',
  'approval_in_progress',
];

/** Off the queues. Same target can be enqueued again. */
export const OFF_QUEUE_STATUSES: AiJobStatus[] = ['cancelled', 'submitted'];

export function isOffQueueStatus(status: AiJobStatus): boolean {
  return status === 'cancelled' || status === 'submitted';
}

const ALLOWED_TRANSITIONS: Record<AiJobStatus, AiJobStatus[]> = {
  spec_queued: ['spec_in_progress', 'spec_ready', 'cancelled'],
  spec_in_progress: ['spec_ready', 'cancelled'],
  spec_ready: ['spec_revision', 'build_queued', 'build_staged', 'cancelled'],
  spec_revision: ['spec_in_progress', 'spec_ready', 'cancelled'],
  build_queued: ['build_in_progress', 'build_ready', 'spec_ready'],
  build_staged: ['build_queued', 'build_revision', 'cancelled'],
  build_in_progress: ['build_ready', 'build_staged'],
  build_ready: ['build_revision', 'approved', 'unwind_queued'],
  build_revision: ['build_in_progress', 'build_ready'],
  unwind_queued: ['unwind_in_progress', 'unwind_ready'],
  unwind_in_progress: ['unwind_ready'],
  unwind_ready: ['unwind_ready'],
  approved: ['approval_in_progress', 'complete'],
  approval_in_progress: ['complete'],
  // Send back to build: the live work stays live; the job re-enters the build queue.
  complete: ['complete', 'submitted', 'build_revision', 'build_staged'],
  submitted: ['build_revision', 'build_staged'],
  cancelled: [],
};

/** Statuses a human can send back to build from. The merged work stays live until the rebuild lands. */
export const REBUILDABLE_STATUSES: AiJobStatus[] = ['complete', 'submitted'];

export type EnqueueAiJobInput = {
  task_id: string;
  title: string;
  card_id?: string | null;
  created_by?: string | null;
  queue_note?: string | null;
  kind?: AiJob['kind'];
  /** The shape of the work; omit for General Fulfillment. */
  task_type?: AiJob['task_type'];
  /** Security Audit jobs: the system audited. */
  security_system_id?: string | null;
  brief?: AiJob['brief'];
  /** One of AI_JOB_MODELS ids. Omit / null = agent default. */
  model?: string | null;
  /** Canned note picked in the spec queue drawer. The database copies its requirements onto the job and enforces them. */
  note_template_id?: string | null;
};

/**
 * Models a human can pin a queue job to. These are the ids OpenClaw has
 * configured and allowlisted; the listener passes the id straight through as
 * `openclaw agent --model <id>`. NULL on the row means "agent default".
 */
export const AI_JOB_MODELS: ReadonlyArray<{ id: string; label: string }> = [
  { id: 'anthropic/claude-opus-5-5', label: 'Claude Opus 5.5' },
  { id: 'anthropic/claude-sonnet-5-5', label: 'Claude Sonnet 5.5' },
  { id: 'anthropic/claude-haiku-5-5', label: 'Claude Haiku 5.5' },
  { id: 'xai/grok-4.7', label: 'Grok 4.7' },
];

/**
 * Models that can no longer be picked but still sit on finished rows. They keep
 * a readable label in history, and a rebuild or deploy of such a job drops the
 * pin so it runs on the agent default instead of the retired model.
 */
const RETIRED_AI_JOB_MODELS: ReadonlyArray<{ id: string; label: string }> = [
  { id: 'anthropic/claude-opus-5', label: 'Claude Opus 5' },
  { id: 'anthropic/claude-fable-5-1', label: 'Claude Fable 5.1' },
  { id: 'anthropic/claude-sonnet-5', label: 'Claude Sonnet 5' },
  { id: 'zai/glm-5.2', label: 'GLM 5.2' },
  { id: 'xai/grok-4.6', label: 'Grok 4.6' },
  { id: 'xai/grok-4.5', label: 'Grok 4.5' },
  { id: 'xai/grok-4.3', label: 'Grok 4.3' },
];

/**
 * NULL model = no --model flag, so the job runs on OpenClaw's
 * `agents.defaults.model.primary`. Set to Opus 5.5 on 2026-09-22; keep this
 * label in step with that config.
 */
export const AI_JOB_MODEL_DEFAULT_LABEL = 'Default · Opus 5.5';

/** Human label for a stored model id. Unknown ids (set by hand / CLI) fall back to the raw id. */
export function aiJobModelLabel(model: string | null | undefined): string {
  if (!model) return AI_JOB_MODEL_DEFAULT_LABEL;
  return (
    AI_JOB_MODELS.find((m) => m.id === model)?.label ??
    RETIRED_AI_JOB_MODELS.find((m) => m.id === model)?.label ??
    model
  );
}

/** A row's model for its next run: a retired pin becomes NULL (agent default), anything else carries over. */
function carryJobModel(model: string | null | undefined): string | null {
  if (!model) return null;
  return RETIRED_AI_JOB_MODELS.some((m) => m.id === model) ? null : model;
}

/** Normalise a picker/enqueue value: only allowlisted ids are written, anything else becomes NULL. */
function normalizeJobModel(model: string | null | undefined): string | null {
  if (!model) return null;
  return AI_JOB_MODELS.some((m) => m.id === model) ? model : null;
}

export type AiQueue = 'spec' | 'build';

export type FetchQueueOptions = {
  queue?: AiQueue;
  status?: AiJobStatus | AiJobStatus[];
  limit?: number;
};

type AiJobPatch = Partial<
  Pick<
    AiJob,
    | 'status'
    | 'spec_body'
    | 'report_body'
    | 'approval_body'
    | 'revision_note'
    | 'queue_note'
    | 'handoff_notes'
    | 'kind'
    | 'brief'
    | 'auto_approve'
    | 'version'
    | 'title'
    | 'claimed_by'
    | 'claimed_at'
    | 'queue_position'
    | 'lock_reason'
    | 'blocked_by_job_id'
    | 'db_ref'
    | 'parked_from'
    | 'model'
    | 'operator_summary'
  >
>;

const BUILD_WATCH_STATUSES: AiJobStatus[] = [
  'build_queued',
  'build_revision',
  'unwind_queued',
  'approved',
];

const SPEC_WATCH_STATUSES: AiJobStatus[] = ['spec_queued', 'spec_revision'];

function compareQueuePosition(a: AiJob, b: AiJob): number {
  const pos = (a.queue_position ?? 0) - (b.queue_position ?? 0);
  if (pos !== 0) return pos;
  return a.created_at.localeCompare(b.created_at);
}

function sortWatchJobs(jobs: AiJob[]): AiJob[] {
  const build = jobs.filter((job) => BUILD_WATCH_STATUSES.includes(job.status)).sort(compareQueuePosition);
  const spec = jobs.filter((job) => SPEC_WATCH_STATUSES.includes(job.status)).sort(compareQueuePosition);
  return [...build, ...spec];
}

function isEnteringBuildQueue(from: AiJobStatus, to: AiJobStatus): boolean {
  return (to === 'build_queued' || to === 'build_staged') && !BUILD_QUEUE_STATUSES.includes(from);
}

async function nextQueuePosition(queue: AiQueue): Promise<number> {
  const statuses = queue === 'spec' ? SPEC_QUEUE_STATUSES : BUILD_QUEUE_STATUSES;
  const { data, error } = await supabase
    .from('ai_jobs')
    .select('queue_position')
    .in('status', statuses)
    .order('queue_position', { ascending: false })
    .limit(1);
  if (error) throw error;
  const max = data?.[0]?.queue_position;
  return (typeof max === 'number' ? max : -1) + 1;
}

function assertTransition(from: AiJobStatus, to: AiJobStatus): void {
  if (!ALLOWED_TRANSITIONS[from].includes(to)) {
    throw new Error(`Invalid ai_jobs transition: ${from} → ${to}`);
  }
}

function isUniqueViolation(error: { code?: string; message?: string }, index: string): boolean {
  if (error.code !== '23505') return false;
  return (error.message ?? '').includes(index);
}

function claimTargetStatus(from: AiJobStatus): AiJobStatus | null {
  if (from === 'spec_queued' || from === 'spec_revision') return 'spec_in_progress';
  if (from === 'build_queued' || from === 'build_revision') return 'build_in_progress';
  if (from === 'unwind_queued') return 'unwind_in_progress';
  if (from === 'approved') return 'approval_in_progress';
  return null;
}

/** A note typed with a forward click. Notes stack on the job: each click with a note adds one, nothing clears them. */
export type HandoffNote = { note: string; by: string | null };

function appendHandoff(current: AiJob, h: HandoffNote | null | undefined, step: HandoffEntry['step']): Pick<AiJobPatch, 'handoff_notes'> {
  const text = (h?.note ?? '').trim();
  if (!text) return {};
  return { handoff_notes: [...(current.handoff_notes ?? []), { note: text, by: h?.by ?? null, at: new Date().toISOString(), step }] };
}

function clearClaim(): Pick<AiJobPatch, 'claimed_by' | 'claimed_at'> {
  return { claimed_by: null, claimed_at: null };
}

/** One job by id, for screens that open a job from the URL. */
export async function fetchJobById(id: string): Promise<AiJob | null> {
  const { data, error } = await supabase.from('ai_jobs').select('*').eq('id', id).maybeSingle();
  if (error) throw error;
  return (data as AiJob | null) ?? null;
}

async function getJobOrThrow(id: string): Promise<AiJob> {
  const { data, error } = await supabase
    .from('ai_jobs')
    .select('*')
    .eq('id', id)
    .single();

  if (error) throw error;
  return data as AiJob;
}

async function updateJob(id: string, patch: AiJobPatch): Promise<AiJob> {
  const { data, error } = await supabase
    .from('ai_jobs')
    .update(patch)
    .eq('id', id)
    .select('*')
    .single();

  if (error) throw error;
  return data as AiJob;
}

export async function fetchJobsByTask(taskId: string): Promise<AiJob[]> {
  const { data, error } = await supabase
    .from('ai_jobs')
    .select('*')
    .eq('task_id', taskId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data ?? []) as AiJob[];
}

export async function fetchQueueJobs(options: FetchQueueOptions = {}): Promise<AiJob[]> {
  let statuses: AiJobStatus[];
  if (options.status) {
    statuses = Array.isArray(options.status) ? options.status : [options.status];
  } else if (options.queue === 'spec') {
    statuses = SPEC_QUEUE_STATUSES;
  } else if (options.queue === 'build') {
    statuses = BUILD_QUEUE_STATUSES;
  } else {
    statuses = OWL_WATCH_STATUSES;
  }

  const isWatch = !options.status && !options.queue;

  // The client rides along so every queue row can say whose it is: the job's own client, else its task's.
  let query = supabase
    .from('ai_jobs')
    .select('*, client:clients(name), task:client_tasks(client:clients(name))')
    .in('status', statuses);

  if (!isWatch) {
    query = query.order('queue_position', { ascending: true }).order('created_at', { ascending: true });
  }

  if (options.limit != null && !isWatch) {
    query = query.limit(options.limit);
  }

  const { data, error } = await query;
  if (error) throw error;
  const rows = (data ?? []) as AiJob[];
  if (isWatch) {
    const sorted = sortWatchJobs(rows);
    return options.limit != null ? sorted.slice(0, options.limit) : sorted;
  }
  return rows;
}

/** Persist drag order. queue_position = index. Same pattern as FulfillmentHQ priority. */
export async function reorderQueueJobs(orderedIds: string[]): Promise<void> {
  const results = await Promise.all(
    orderedIds.map((id, index) =>
      supabase.from('ai_jobs').update({ queue_position: index }).eq('id', id)
    )
  );
  const failed = results.find((result) => result.error);
  if (failed?.error) throw failed.error;
}

export async function enqueueAiJob(input: EnqueueAiJobInput): Promise<AiJob> {
  const queue_position = await nextQueuePosition('spec');
  const { data, error } = await supabase
    .from('ai_jobs')
    .insert({
      task_id: input.task_id,
      title: input.title,
      card_id: input.card_id ?? null,
      created_by: input.created_by ?? null,
      status: 'spec_queued',
      spec_body: '',
      report_body: '',
      approval_body: '',
      revision_note: '',
      queue_note: (input.queue_note ?? '').trim(),
      kind: input.kind ?? 'fulfillment',
      task_type: input.task_type ?? 'general',
      ...(input.security_system_id ? { security_system_id: input.security_system_id } : {}),
      brief: input.brief ?? {},
      version: 1,
      queue_position,
      model: normalizeJobModel(input.model),
      ...(input.note_template_id ? { note_template_id: input.note_template_id } : {}),
    })
    .select('*')
    .single();

  if (error) {
    if (isUniqueViolation(error, 'ai_jobs_open_target')) {
      throw new Error('This item is already in the agent queues');
    }
    if (input.task_type === 'security_audit' && (error.code === '23505' || /security audit/i.test(error.message))) {
      throw new Error(/already has an audit open/i.test(error.message) ? error.message : 'This system already has an audit open. Finish or cancel it first.');
    }
    throw error;
  }
  return data as AiJob;
}

export async function transitionAiJob(
  id: string,
  to: AiJobStatus,
  patch: Omit<AiJobPatch, 'status'> = {}
): Promise<AiJob> {
  const current = await getJobOrThrow(id);
  assertTransition(current.status, to);
  const nextPatch: AiJobPatch = { ...patch, status: to };
  if (!IN_PROGRESS_STATUSES.includes(to)) {
    nextPatch.claimed_by = patch.claimed_by === undefined ? null : patch.claimed_by;
    nextPatch.claimed_at = patch.claimed_at === undefined ? null : patch.claimed_at;
  }
  if (isEnteringBuildQueue(current.status, to)) {
    nextPatch.queue_position = await nextQueuePosition('build');
  }
  return updateJob(id, nextPatch);
}

/** Agent pickup. get is peeking and must not call this. */
export async function claimAiJob(id: string, agentName: string): Promise<AiJob> {
  const name = agentName.trim();
  if (!name) throw new Error('Agent name is required to claim a job');

  const current = await getJobOrThrow(id);
  if (IN_PROGRESS_STATUSES.includes(current.status)) {
    if ((current.claimed_by || '').toLowerCase() === name.toLowerCase()) {
      return current;
    }
    throw new Error(`Job is already in progress (${current.claimed_by || 'another agent'})`);
  }

  const next = claimTargetStatus(current.status);
  if (!next) {
    throw new Error(`Cannot claim a job in status ${current.status}`);
  }
  assertTransition(current.status, next);

  const now = new Date().toISOString();
  const { data, error } = await supabase
    .from('ai_jobs')
    .update({
      status: next,
      claimed_by: name,
      claimed_at: now,
    })
    .eq('id', id)
    .in('status', CLAIMABLE_STATUSES)
    .select('*')
    .maybeSingle();

  if (error) throw error;
  if (!data) {
    throw new Error('Could not claim this job — it may have just been claimed');
  }
  return data as AiJob;
}

/** Owlbert submits / resubmits a spec → spec_ready, bump version, clear revision_note.
    Declared path locks are written first so auto-approve can acquire them. */
export async function submitSpec(id: string, specBody: string): Promise<AiJob> {
  const current = await getJobOrThrow(id);
  assertTransition(current.status, 'spec_ready');
  await setDeclaredLocksFromSpec(id, specBody);
  return updateJob(id, {
    status: 'spec_ready',
    spec_body: specBody,
    revision_note: '',
    version: current.version + 1,
    ...clearClaim(),
  });
}

/** Owlbert submits / resubmits a build report → build_ready, bump version, clear revision_note. */
export async function submitReport(id: string, reportBody: string): Promise<AiJob> {
  const current = await getJobOrThrow(id);
  assertTransition(current.status, 'build_ready');
  return updateJob(id, {
    status: 'build_ready',
    report_body: reportBody,
    revision_note: '',
    version: current.version + 1,
    ...clearClaim(),
  });
}

/** Agent unwind report after a ready build was cancelled. Does not delete the job. */
export async function submitUnwind(id: string, reportBody: string): Promise<AiJob> {
  const current = await getJobOrThrow(id);
  if (current.status === 'unwind_ready') {
    return updateJob(id, {
      status: 'unwind_ready',
      report_body: reportBody,
      version: current.version + 1,
      ...clearClaim(),
    });
  }
  assertTransition(current.status, 'unwind_ready');
  return updateJob(id, {
    status: 'unwind_ready',
    report_body: reportBody,
    version: current.version + 1,
    ...clearClaim(),
  });
}

/** Human reply: spec_ready → spec_revision or build_ready → build_revision. */
export async function reviseAiJob(id: string, note: string): Promise<AiJob> {
  const current = await getJobOrThrow(id);
  if (current.status === 'spec_ready') {
    return updateJob(id, { status: 'spec_revision', revision_note: note, ...clearClaim() });
  }
  if (current.status === 'build_ready') {
    return updateJob(id, { status: 'build_revision', revision_note: note, ...clearClaim() });
  }
  throw new Error(`Cannot revise ai_job in status ${current.status}`);
}

/**
 * Model the queue listener routes image jobs to (`openclaw agent --model`).
 *
 * This is the agent that writes the prompts and drives the run, not the model
 * that draws — the pictures come from `xai/grok-imagine-image` either way.
 * Moved off `xai/grok-4.6` on 2026-09-15: the same nine-slot run took 58
 * minutes where a twelve-slot run had taken 25, which is the signature of
 * working through slots in series instead of fanning them out. Moved from
 * Opus 5 to Opus 5.5 on 2026-09-22 when Opus 5 left the picker.
 */
export const IMAGE_BUILD_MODEL = 'anthropic/claude-opus-5-5';

/**
 * Model hosting-fix jobs ("Send to agent queue" on the hosting screen) are
 * pinned to when they're created. Moved off the agent default (Opus 5.5) on
 * 2026-10-13 at Kyle's request. Changeable from the queue card like any pin.
 */
export const HOSTING_FIX_MODEL = 'anthropic/claude-sonnet-5-5';

/**
 * Model Security Audit jobs (Audit button on Security → Audits) are pinned to
 * when they're created. Moved off the agent default (Opus 5.5) on 2026-10-24
 * at Kyle's request. Changeable from the queue card like any pin.
 */
export const SECURITY_AUDIT_MODEL = 'xai/grok-4.7';

/** `brief.image_request` — the slots a content build should generate options for. */
export type ImageRequest = { piece_id: string; slots: string[] };

/**
 * Human: send a content build back for image generation. Same path as a
 * build_ready → build_revision reply, plus the routing hook: ai_jobs.model
 * is set and image_request is merged into the existing brief so the agent
 * that picks it up sees which slots to fill. Writes `model` directly (not via
 * the allowlist) so the image route stays pinned to IMAGE_BUILD_MODEL.
 */
export async function requestImageBuild(
  id: string,
  request: ImageRequest,
  model: string = IMAGE_BUILD_MODEL
): Promise<AiJob> {
  const current = await getJobOrThrow(id);
  assertTransition(current.status, 'build_revision');
  if (request.slots.length === 0) throw new Error('No image slots to generate');
  const n = request.slots.length;
  const revision_note = `Generate 3 options for ${n} image slot${n === 1 ? '' : 's'}: ${request.slots.join(', ')}`;
  const brief = { ...(current.brief as Record<string, unknown>), image_request: request };
  const { data, error } = await (supabase as unknown as LooseClient)
    .from('ai_jobs')
    .update({ status: 'build_revision', revision_note, brief, model, ...clearClaim() })
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  return data as AiJob;
}

/**
 * `brief.deploy_request` — the finished article a build should push into the
 * client's CMS, and the knowledge file that says how. `kb_file_id` is carried
 * so the agent reads the same playbook the button checked for, rather than
 * searching the client's library again and picking a superseded copy.
 */
export type DeployRequest = {
  piece_id: string;
  piece_title: string;
  kb_file_id: string;
  kb_title: string;
};

/**
 * Human: deploy a finished content piece. Same transition as "send back to
 * build" — complete / submitted → build_revision, or build_staged when another
 * build holds a lock on the same repo or database — plus the routing hook:
 * `deploy_request` is merged into the existing brief so the agent that picks it
 * up knows which piece to publish and which knowledge file to follow.
 *
 * The model is deliberately left alone. Unlike an image build there is no one
 * right model for touching a live client CMS; whatever the job already runs on
 * (or the agent default) carries over. A retired pin falls back to the default.
 */
export async function requestArticleDeploy(id: string, request: DeployRequest): Promise<AiJob> {
  const current = await getJobOrThrow(id);
  if (!REBUILDABLE_STATUSES.includes(current.status)) {
    throw new Error(`Cannot deploy from status ${current.status}; the job has to be finished first`);
  }
  const acq = await tryAcquireJobLocks(id);
  const to: AiJobStatus = acq.ok ? 'build_revision' : 'build_staged';
  assertTransition(current.status, to);
  const revision_note = [
    `Deploy "${request.piece_title}" to the client's CMS.`,
    `Follow the "${request.kb_title}" knowledge file (cockpit kb get ${request.kb_file_id}) — it is the only source for schema, model, image handling and SEO.`,
    `Read the article: cockpit content get ${request.piece_id} --json`,
  ].join('\n');
  const brief = { ...(current.brief as Record<string, unknown>), deploy_request: request };
  const patch: AiJobPatch = {
    status: to,
    revision_note,
    model: carryJobModel(current.model),
    lock_reason: acq.ok ? '' : (acq.reason || 'Waiting on another build lock'),
    blocked_by_job_id: acq.ok ? null : acq.blocked_by ?? null,
    parked_from: acq.ok ? null : current.status,
    ...clearClaim(),
  };
  if (isEnteringBuildQueue(current.status, to)) {
    patch.queue_position = await nextQueuePosition('build');
  }
  const { data, error } = await (supabase as unknown as LooseClient)
    .from('ai_jobs')
    .update({ ...patch, brief })
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  return data as AiJob;
}

/** Decision id → the human's answer, written into the spec summary at approve time. */
export type DecisionAnswers = Record<string, string>;

/** Human: spec_ready → build_queued, or build_staged if a path or DB ref is held.
    Decision answers (from the operator page) land in operator_summary.spec.decisions
    first, so the build agent reads them with `cockpit ai get`, as it does every note sent with a forward click. */
export async function approveSpec(id: string, answers: DecisionAnswers = {}, handoff?: HandoffNote | null): Promise<AiJob> {
  const current = await getJobOrThrow(id);
  if (current.spec_body) {
    await setDeclaredLocksFromSpec(id, current.spec_body);
  }
  const acq = await tryAcquireJobLocks(id);
  const to: AiJobStatus = acq.ok ? 'build_queued' : 'build_staged';
  assertTransition(current.status, to);
  const nextPatch: AiJobPatch = {
    status: to,
    lock_reason: acq.ok ? '' : (acq.reason || 'Waiting on another build lock'),
    blocked_by_job_id: acq.ok ? null : acq.blocked_by ?? null,
    ...clearClaim(),
    ...appendHandoff(current, handoff, 'spec_approved'),
  };
  const spec = current.operator_summary?.spec;
  if (spec && spec.decisions.length > 0 && Object.keys(answers).length > 0) {
    nextPatch.operator_summary = {
      ...current.operator_summary,
      spec: {
        ...spec,
        decisions: spec.decisions.map((d) => (answers[d.id] !== undefined ? { ...d, answer: answers[d.id] } : d)),
      },
    };
  }
  if (isEnteringBuildQueue(current.status, to)) {
    nextPatch.queue_position = await nextQueuePosition('build');
  }
  const updated = await updateJob(id, nextPatch);
  // Every answer on record at approve, not only the ones sent with the click: an Opus review (src/lib/specReview.ts)
  // pre-fills answers, and the approve click is what makes them Kyle's cascade calls.
  const finalAnswers: Record<string, string> = {};
  for (const d of updated.operator_summary?.spec?.decisions ?? []) {
    if (d.answer?.trim()) finalAnswers[d.id] = d.answer.trim();
  }
  if (Object.keys(finalAnswers).length) await recordCascadeCalls(updated, finalAnswers);
  return updated;
}

/** Human: record the answer to one spec-summary decision, in place, from the job
    page. Pass null to un-answer. The build agent reads these when it claims the
    job, so this stays editable until the job ships — answering late but before
    pickup still reaches the builder. Locked once the work is live. */
export async function answerSpecDecision(
  id: string,
  decisionId: string,
  answer: string | null
): Promise<AiJob> {
  const current = await getJobOrThrow(id);
  if (isOffQueueStatus(current.status) || current.status === 'complete') {
    throw new Error(`Cannot change a decision in status ${current.status}`);
  }
  const spec = current.operator_summary?.spec;
  if (!spec) throw new Error('This job has no spec summary, so there are no decisions to answer');
  if (!spec.decisions.some((d) => d.id === decisionId)) {
    throw new Error(`Unknown decision ${decisionId}`);
  }
  const value = answer && answer.trim() ? answer.trim() : null;
  const updated = await updateJob(id, {
    operator_summary: {
      ...current.operator_summary,
      spec: {
        ...spec,
        decisions: spec.decisions.map((d) => (d.id === decisionId ? { ...d, answer: value } : d)),
      },
    },
  });
  await recordCascadeCalls(updated, { [decisionId]: value });
  return updated;
}

/** Human: save an adjusted article outline on a content job, or pass null to go
    back to the agent's. Same window as decision answers: the build agent reads it
    with `cockpit ai get` when it claims the job, so it stays editable until the
    job ships. */
export async function setOutlineOverride(
  id: string,
  headings: OutlineHeading[] | null,
  note?: string | null
): Promise<AiJob> {
  const current = await getJobOrThrow(id);
  if (isOffQueueStatus(current.status) || current.status === 'complete') {
    throw new Error(`Cannot change the outline in status ${current.status}`);
  }
  const spec = current.operator_summary?.spec;
  if (!spec) throw new Error('This job has no spec summary, so there is no outline to adjust');
  const clean = (headings ?? [])
    .map((h) => ({ ...h, text: h.text.trim(), intent: h.intent?.trim() || undefined }))
    .filter((h) => h.text);
  return updateJob(id, {
    operator_summary: {
      ...current.operator_summary,
      spec: {
        ...spec,
        outline_override: clean.length
          ? { headings: clean, note: note?.trim() || null, updated_at: new Date().toISOString() }
          : null,
      },
    },
  });
}

/** Human: save edited Page Build sections, or null to go back to the agent's. Same window as the outline: the build
    agent reads spec.sections_override with `cockpit ai get` and ships those sections, so it stays editable until the
    job ships. */
export async function setSectionsOverride(id: string, sections: PageSection[] | null): Promise<AiJob> {
  const current = await getJobOrThrow(id);
  if (isOffQueueStatus(current.status) || current.status === 'complete') {
    throw new Error(`Cannot change the sections in status ${current.status}`);
  }
  const spec = current.operator_summary?.spec;
  if (!spec?.sections?.length) throw new Error('This job has no sections to edit');
  const clean = (sections ?? [])
    .map((s) => ({
      ...s,
      heading: s.heading.trim(),
      purpose: (s.purpose ?? '').trim(),
      message: (s.message ?? '').trim(),
      source: s.source?.trim() || null,
      image: s.image?.idea?.trim() ? { idea: s.image.idea.trim() } : null,
      signature: !!s.signature,
    }))
    .filter((s) => s.heading || s.message);
  return updateJob(id, {
    operator_summary: {
      ...current.operator_summary,
      spec: { ...spec, sections_override: clean.length ? { sections: clean, updated_at: new Date().toISOString() } : null },
    },
  });
}

/** Human: change a Page Build's target keyword before it ships, or null to go back to the agent's pick. The build
    agent reads spec.keyword_override with `cockpit ai get`; the section prompts and the SEO review use it. */
export async function setKeywordOverride(id: string, keyword: { primary: string; secondary: string[] } | null): Promise<AiJob> {
  const current = await getJobOrThrow(id);
  if (isOffQueueStatus(current.status) || current.status === 'complete') {
    throw new Error(`Cannot change the keyword in status ${current.status}`);
  }
  const spec = current.operator_summary?.spec;
  if (!spec) throw new Error('This job has no spec yet');
  const primary = keyword?.primary.trim() ?? '';
  const secondary = (keyword?.secondary ?? []).map((s) => s.trim()).filter(Boolean);
  return updateJob(id, {
    operator_summary: {
      ...current.operator_summary,
      spec: { ...spec, keyword_override: primary ? { primary, secondary, updated_at: new Date().toISOString() } : null },
    },
  });
}

/** Human: complete / submitted → build_revision (or build_staged while a path or DB
    ref is held by another build). The merged work stays live; the note tells the
    build agent what to change. The old report, approval, and summaries stay on the
    row and in history. A ticked comm chip is left alone. */
export async function sendBackToBuild(id: string, note: string): Promise<AiJob> {
  const current = await getJobOrThrow(id);
  if (!REBUILDABLE_STATUSES.includes(current.status)) {
    throw new Error(`Cannot send a job back to build from status ${current.status}`);
  }
  const text = note.trim();
  if (!text) throw new Error('Say what the rebuild should change');
  const acq = await tryAcquireJobLocks(id);
  const to: AiJobStatus = acq.ok ? 'build_revision' : 'build_staged';
  assertTransition(current.status, to);
  const nextPatch: AiJobPatch = {
    status: to,
    revision_note: text,
    model: carryJobModel(current.model),
    lock_reason: acq.ok ? '' : (acq.reason || 'Waiting on another build lock'),
    blocked_by_job_id: acq.ok ? null : acq.blocked_by ?? null,
    // promote_staged_builds sends a parked job to build_revision when parked_from is set.
    parked_from: acq.ok ? null : current.status,
    ...clearClaim(),
  };
  if (isEnteringBuildQueue(current.status, to)) {
    nextPatch.queue_position = await nextQueuePosition('build');
  }
  return updateJob(id, nextPatch);
}

/** Human: turn a parked item from the operator summary into its own Cockpit task
    on the job's client, assigned to whoever clicked, due today unless they pick
    another date. The description carries the trail back to the job it came from,
    because that description is what an agent reads when the task is queued. */
export async function createTaskFromParked(
  job: AiJob,
  text: string,
  userId: string,
  /** yyyy-MM-dd, the same shape the Create Task panel writes. Defaults to today. */
  dueDate?: string
): Promise<{ id: string }> {
  if (!job.client_id) throw new Error('This job has no client, so there is nowhere to file the task');
  const clean = text.trim();
  if (!clean) throw new Error('Nothing to file');
  const title = clean.length > 90 ? `${clean.slice(0, 87).trimEnd()}…` : clean;
  const due = dueDate?.trim() || formatForDateInput(new Date());
  const { data: who } = await supabase.from('users').select('name').eq('id', userId).maybeSingle();
  const cardUrl = job.card_id ? await fetchTrelloCardUrl(job.card_id) : null;
  const description = [
    clean,
    '',
    `Parked while working the agent-queue job "${job.title}".`,
    'Full context — the spec, why this was set aside, and what shipped instead:',
    `  cockpit ai get ${job.id}`,
    cardUrl ? `Client card: ${cardUrl}` : null,
  ]
    .filter((line): line is string => line !== null)
    .join('\n');
  const { data, error } = await (supabase as unknown as LooseClient)
    .from('client_tasks')
    .insert({
      client_id: job.client_id,
      title,
      description,
      status: 'Scheduled',
      due_date: due,
      assigned_by: userId,
      assigned_to: userId,
      assigned_to_name: who?.name ?? null,
    })
    .select('id')
    .single();
  if (error) throw error;
  return data as { id: string };
}

/** Human: build_ready → approved. Stays on the board. Does not merge PRs or apply migrations. */
export async function approveBuild(id: string, handoff?: HandoffNote | null): Promise<AiJob> {
  const current = await getJobOrThrow(id);
  return transitionAiJob(id, 'approved', appendHandoff(current, handoff, 'build_approved'));
}

/** Human edits the paste-ready client note on the approval summary. Allowed on
    `complete` and `submitted` — that is when the note is actually read — and it
    does not bump the job version, because rewording the note is a correction to
    the existing approval, not a new one. */
export async function updateClientNote(id: string, note: string): Promise<AiJob> {
  const current = await getJobOrThrow(id);
  if (isOffQueueStatus(current.status)) {
    throw new Error(`Cannot edit the client note in status ${current.status}`);
  }
  const approval = current.operator_summary?.approval;
  if (!approval) throw new Error('This job has no approval summary, so there is no client note');
  const value = note.trim();
  if (!value) throw new Error('The client note cannot be empty');
  return updateJob(id, {
    operator_summary: {
      ...current.operator_summary,
      approval: { ...approval, client_note: value },
    },
  });
}

/** Agent approval report after live merge/migrate/deploy → complete. */
export async function submitApproval(id: string, approvalBody: string): Promise<AiJob> {
  const current = await getJobOrThrow(id);
  if (current.status === 'complete') {
    return updateJob(id, {
      status: 'complete',
      approval_body: approvalBody,
      version: current.version + 1,
      ...clearClaim(),
    });
  }
  assertTransition(current.status, 'complete');
  return updateJob(id, {
    status: 'complete',
    approval_body: approvalBody,
    version: current.version + 1,
    ...clearClaim(),
  });
}

/** Human: complete → submitted. Leaves the queue, ticks the linked comm chip. Locks already released at complete. */
export async function submitApprovedBuild(id: string): Promise<AiJob> {
  const current = await getJobOrThrow(id);
  if (current.status !== 'complete') {
    throw new Error(`Cannot mark submitted from status ${current.status}`);
  }
  assertTransition(current.status, 'submitted');
  const updated = await updateJob(id, { status: 'submitted', ...clearClaim() });
  if (current.card_id) {
    const { error } = await supabase
      .from('comms_task_links')
      .update({ completed_at: new Date().toISOString() })
      .eq('task_id', current.task_id)
      .eq('card_id', current.card_id)
      .is('completed_at', null);
    if (error) throw error;
  }
  return updated;
}

/** Human: flag/unflag a spec-phase job to skip review. When flagged, the DB
    trigger rewrites spec_ready → build_queued (or staged on a path/DB lock). */
export async function setAutoApprove(id: string, on: boolean): Promise<AiJob> {
  const current = await getJobOrThrow(id);
  if (!['spec_queued', 'spec_in_progress', 'spec_revision'].includes(current.status)) {
    throw new Error(`Cannot change auto-approve in status ${current.status}`);
  }
  return updateJob(id, { auto_approve: on });
}

/** Human: edit the queue note while the job waits for pickup. The agent reads
    it into context when it claims the task. */
export async function updateQueueNote(id: string, note: string): Promise<AiJob> {
  const current = await getJobOrThrow(id);
  if (current.status !== 'spec_queued' && current.status !== 'build_queued') {
    throw new Error(`Queue note can only be edited while queued (status ${current.status})`);
  }
  return updateJob(id, { queue_note: note.trim() });
}

/** Human: pin the job to a model (or NULL for the agent default). The listener
    reads ai_jobs.model at claim time, so this takes effect on the next pickup.
    Rejected once an agent holds the job or it has left the board. */
export async function setAiJobModel(id: string, model: string | null): Promise<AiJob> {
  const current = await getJobOrThrow(id);
  if (isOffQueueStatus(current.status) || current.status === 'complete') {
    throw new Error(`Cannot change model in status ${current.status}`);
  }
  if (IN_PROGRESS_STATUSES.includes(current.status)) {
    throw new Error('An agent is already running this job — the model is locked until it hands back');
  }
  if (model && !AI_JOB_MODELS.some((m) => m.id === model)) {
    throw new Error(`Unknown model ${model}`);
  }
  return updateJob(id, { model: model || null });
}

/** Human: send a queued build back to spec review. Releases this job's path/DB
    locks (the oldest compatible staged build promotes via the DB trigger). Lands at the
    bottom of the spec queue. */
export async function sendBuildBackToSpec(id: string): Promise<AiJob> {
  const current = await getJobOrThrow(id);
  if (current.status !== 'build_queued') {
    throw new Error(`Cannot send a job back to spec from status ${current.status}`);
  }
  assertTransition(current.status, 'spec_ready');
  const queue_position = await nextQueuePosition('spec');
  return updateJob(id, { status: 'spec_ready', queue_position, ...clearClaim() });
}

/** Human: pull a spec-phase job off the queue. Same target can be enqueued again. */
export async function cancelAiJob(id: string): Promise<AiJob> {
  const current = await getJobOrThrow(id);
  if (!CANCELABLE_SPEC_STATUSES.includes(current.status)) {
    throw new Error(`Cannot cancel a job in status ${current.status}`);
  }
  return updateJob(id, { status: 'cancelled', ...clearClaim() });
}

/** Human: cancel a ready build. Does not delete. Puts it on the unwind queue. */
export async function cancelReadyBuild(id: string, note?: string): Promise<AiJob> {
  const current = await getJobOrThrow(id);
  if (!CANCELABLE_BUILD_STATUSES.includes(current.status)) {
    throw new Error(`Cannot cancel a ready build in status ${current.status}`);
  }
  return updateJob(id, {
    status: 'unwind_queued',
    revision_note: (note ?? '').trim(),
    ...clearClaim(),
  });
}

/** Human: permanently delete a job after unwind is ready. */
export async function deleteUnwoundJob(id: string): Promise<void> {
  const current = await getJobOrThrow(id);
  if (!DELETABLE_STATUSES.includes(current.status)) {
    throw new Error(`Cannot delete a job in status ${current.status}. Unwind must be ready first.`);
  }
  const { error } = await supabase.from('ai_jobs').delete().eq('id', id);
  if (error) throw error;
}
